import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../constants/accountSettingsPages';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  IconProfile,
  IconCart,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';
import BrandsMegaMenu from './CustomLinksMenu/BrandsMegaMenu';
import CategoriesMegaMenu from './CustomLinksMenu/CategoriesMegaMenu';
import CompatibilityMegaMenu from './CustomLinksMenu/CompatibilityMegaMenu';

import css from './TopbarDesktop.module.css';

const ProfileIconLink = () => {
  return (
    <NamedLink name="LoginPage" className={css.profileMenuLabel}>
      <span className={css.topbarLinkLabel}>
        <IconProfile className={css.profileIcon} />
      </span>
    </NamedLink>
  );
};

const ProfileAvatarLink = () => {
  return (
    <NamedLink name="ProfileDashboardPage" className={css.profileMenuLabel}>
      <span className={css.topbarLinkLabel}>
        <IconProfile className={css.profileIcon} filled />
      </span>
    </NamedLink>
  );
};

const InboxLink = ({ notificationCount, inboxTab }) => {
  return (
    <NamedLink className={css.topbarLink} name="InboxPage" params={{ tab: inboxTab }}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.inbox" />
      </span>
    </NamedLink>
  );
};

const CartLink = ({ notificationCount = 0 }) => {
  const notificationBadge =
    notificationCount > 0 ? (
      <div className={css.cartNotificationBadge}>
        {notificationCount > 99 ? '99+' : notificationCount}
      </div>
    ) : null;

  return (
    <NamedLink name="CartPage" className={css.cartIconLink}>
      <IconCart className={css.cartIcon} size="small" />
      {notificationBadge}
    </NamedLink>
  );
};

const ProfileMenu = ({ currentPage, currentUser, onLogout, showManageListingsLink }) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  // All users (both customer and provider) now use the same profile menu styling
  const profileMenuLabelClass = css.profileMenuLabel;
  const isProvider = currentUser?.attributes?.profile?.publicData?.userType === 'provider';

  return (
    <Menu>
      <MenuLabel className={profileMenuLabelClass} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        {showManageListingsLink ? (
          <MenuItem key="ManageListingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </NamedLink>
          </MenuItem>
        ) : null}
        {isProvider ? (
          <MenuItem key="SalesPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('SalesPage'))}
              name="SalesPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.salesLink" />
            </NamedLink>
          </MenuItem>
        ) : null}
        <MenuItem key="MyRequestsPage">
          <NamedLink
            className={classNames(
              css.menuLink,
              currentPageClass('MyRequestsPage'),
              currentPageClass('NewSupportRequestPage'),
              currentPageClass('SupportRequestDetailPage')
            )}
            name="MyRequestsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id={isProvider ? 'UserNav.sellerRequests' : 'UserNav.myRequests'} />
          </NamedLink>
        </MenuItem>
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="ContactDetailsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ContactDetailsPage'))}
            name="ContactDetailsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {number} props.cartCount
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showSearchForm
 * @param {boolean} props.showCreateListingsLink
 * @param {string} props.inboxTab
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    notificationCount = 0,
    cartCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    showSearchForm,
    showCreateListingsLink,
    inboxTab,
    history,
    routeConfiguration,
  } = props;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);

  // Track which topbar mega menu is open (brands | categories | compatibility)
  const [activeMegaMenuId, setActiveMegaMenuId] = useState(null);

  const inboxLinkMaybe = null; // Removed inbox button for logged-in users

  const profileMenuMaybe = null; // Dropdown disabled

  const profileAvatarLinkMaybe = authenticatedOnClientSide ? (
    <ProfileAvatarLink currentUser={currentUser} />
  ) : null;

  const profileIconLinkMaybe = isAuthenticatedOrJustHydrated ? null : <ProfileIconLink />;

  const searchFormMaybe = showSearchForm ? (
    <TopbarSearchForm
      className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={config}
    />
  ) : (
    <div
      className={classNames(css.spacer, css.topbarSearchWithLeftPadding, {
        [css.takeAvailableSpace]: giveSpaceForSearch,
      })}
    />
  );

  return (
    <nav className={classes}>
      <LinkedLogo
        className={css.logoLink}
        layout="desktop"
        alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
        linkToExternalSite={config?.topbar?.logoLink}
      />
      {searchFormMaybe}

      <CustomLinksMenu
        currentPage={currentPage}
        customLinks={customLinks}
        intl={intl}
        hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
        showCreateListingsLink={showCreateListingsLink}
      />

      <BrandsMegaMenu
        className={css.rightSectionStart}
        currentPage={currentPage}
        intl={intl}
        history={history}
        routeConfiguration={routeConfiguration}
        isOpen={activeMegaMenuId === 'brands'}
        onRequestOpen={() => setActiveMegaMenuId('brands')}
        onRequestClose={() => setActiveMegaMenuId(null)}
      />
      <CategoriesMegaMenu
        currentPage={currentPage}
        intl={intl}
        history={history}
        routeConfiguration={routeConfiguration}
        isOpen={activeMegaMenuId === 'categories'}
        onRequestOpen={() => setActiveMegaMenuId('categories')}
        onRequestClose={() => setActiveMegaMenuId(null)}
      />
      <CompatibilityMegaMenu
        currentPage={currentPage}
        intl={intl}
        history={history}
        routeConfiguration={routeConfiguration}
        isOpen={activeMegaMenuId === 'compatibility'}
        onRequestOpen={() => setActiveMegaMenuId('compatibility')}
        onRequestClose={() => setActiveMegaMenuId(null)}
      />
      {inboxLinkMaybe}
      <CartLink notificationCount={cartCount} />
      {profileAvatarLinkMaybe}
      {profileIconLinkMaybe}
    </nav>
  );
};

export default TopbarDesktop;
